import type { BoxProps } from '@chakra-ui/react';
import { Box, Image } from '@chakra-ui/react';
import React from 'react';

// Source images are 1672x941 with an opaque background and wide padding around the wordmark,
// so each variant is cropped to its wordmark bounds (in source pixels)
// and blended into the footer background instead of showing a black or white rectangle.
const SOURCE = { width: 1672, height: 941 };

const VARIANTS = {
  dark: {
    src: 'https://storage.googleapis.com/public.tokenrize.io/Tokenrize/DarkCarbonZero.png',
    crop: { x: 196, y: 400, width: 1280, height: 112 },
    blendMode: 'lighten',
  },
  light: {
    src: 'https://storage.googleapis.com/public.tokenrize.io/Tokenrize/LightCarbonZero.png',
    crop: { x: 144, y: 398, width: 1384, height: 132 },
    blendMode: 'darken',
  },
} as const;

type Variant = typeof VARIANTS[keyof typeof VARIANTS];

const LogoVariant = ({ variant, ...rest }: { variant: Variant } & BoxProps) => {
  const { crop } = variant;
  return (
    <Box
      position="relative"
      w="100%"
      overflow="hidden"
      sx={{ aspectRatio: `${ crop.width } / ${ crop.height }` }}
      { ...rest }
    >
      <Image
        src={ variant.src }
        alt="CarbonZero"
        loading="lazy"
        draggable={ false }
        position="absolute"
        maxW="none"
        w={ `${ SOURCE.width / crop.width * 100 }%` }
        left={ `${ -crop.x / crop.width * 100 }%` }
        top={ `${ -crop.y / crop.height * 100 }%` }
        mixBlendMode={ variant.blendMode }
      />
    </Box>
  );
};

// Both variants are rendered and toggled with CSS so the right one shows on first paint, without a hydration flash.
const CarbonZeroLogo = (props: BoxProps) => {
  return (
    <Box w="168px" { ...props }>
      <LogoVariant variant={ VARIANTS.light } _dark={{ display: 'none' }}/>
      <LogoVariant variant={ VARIANTS.dark } display="none" _dark={{ display: 'block' }}/>
    </Box>
  );
};

export default React.memo(CarbonZeroLogo);
